import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { RoleError, RoleGateway } from '../../../gateways/role.gateway';
import { Role, CreateRoleRequest, UpdateRoleRequest } from '../../../models/role.model';

type FormMode = 'create' | 'edit';

@Component({
    selector: 'app-role-form',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, RouterLink],
    template: `
        <div class="page-content">
            <h1>{{ mode === 'edit' ? 'Edit role' : 'New role' }}</h1>

            @if (state() === 'loading') {
                <div class="state" role="status" aria-live="polite">Loading role…</div>
            }

            @if (state() === 'not-found') {
                <div class="state state-error" role="alert">
                    <p>This role does not exist or has been deleted.</p>
                    <a class="btn" [routerLink]="['../../../roles']">Back to roles</a>
                </div>
            }

            @if (state() === 'ready') {
                @if (submitError()) {
                    <div class="state state-error" role="alert">{{ submitError() }}</div>
                }

                <form [formGroup]="form" (ngSubmit)="submit()" novalidate>
                    <div class="field">
                        <label for="role-name">Name</label>
                        <input
                            id="role-name"
                            type="text"
                            formControlName="name"
                            [attr.aria-invalid]="isInvalid('name')"
                            [attr.aria-describedby]="isInvalid('name') ? 'role-name-error' : null"
                        />
                        @if (isInvalid('name')) {
                            <p id="role-name-error" class="field-error" role="alert">
                                Name is required.
                            </p>
                        }
                    </div>

                    <div class="field">
                        <label for="role-description">Description (optional)</label>
                        <textarea
                            id="role-description"
                            rows="3"
                            maxlength="256"
                            formControlName="description"
                            [attr.aria-invalid]="isInvalid('description')"
                            [attr.aria-describedby]="
                                isInvalid('description') ? 'role-description-error' : null
                            "
                        ></textarea>
                        @if (isInvalid('description')) {
                            <p id="role-description-error" class="field-error" role="alert">
                                Description must be 256 characters or fewer.
                            </p>
                        }
                    </div>

                    <div class="form-actions">
                        <a class="btn" [routerLink]="cancelLink()">Cancel</a>
                        <button
                            type="submit"
                            class="btn btn-primary"
                            [disabled]="form.invalid || submitting()"
                        >
                            {{
                                submitting()
                                    ? 'Saving…'
                                    : mode === 'edit'
                                      ? 'Save changes'
                                      : 'Create role'
                            }}
                        </button>
                    </div>
                </form>
            }
        </div>
    `,
    styles: [
        `
            .page-content {
                padding: 2rem;
                max-width: 640px;
            }
            h1 {
                font-size: 1.75rem;
                font-weight: 600;
                margin-bottom: 1.5rem;
                color: #1a1a1a;
            }
            .field {
                margin-bottom: 1.25rem;
                display: flex;
                flex-direction: column;
            }
            label {
                font-weight: 600;
                margin-bottom: 0.4rem;
                color: #445;
            }
            input,
            textarea {
                padding: 0.6rem 0.75rem;
                border: 1px solid #d0d0d0;
                border-radius: 6px;
                font: inherit;
                background: white;
            }
            input[aria-invalid='true'],
            textarea[aria-invalid='true'] {
                border-color: #e0b4b4;
            }
            .field-error {
                color: #a12622;
                margin: 0.4rem 0 0;
                font-size: 0.85rem;
            }
            .form-actions {
                display: flex;
                gap: 0.5rem;
                justify-content: flex-end;
                margin-top: 1.5rem;
            }
            .state {
                padding: 2rem;
                background: white;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                color: #666;
            }
            .state-error {
                border-color: #f5c2c2;
                background: #fdecea;
                color: #a12622;
            }
            .btn {
                display: inline-block;
                padding: 0.5rem 0.9rem;
                border-radius: 6px;
                border: 1px solid #d0d0d0;
                background: white;
                color: #1a1a1a;
                text-decoration: none;
                cursor: pointer;
                font-size: 0.9rem;
            }
            .btn:hover {
                background: #f5f5f5;
            }
            .btn-primary {
                background: #2196f3;
                border-color: #2196f3;
                color: white;
            }
            .btn-primary:hover {
                background: #1976d2;
            }
            .btn:disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }
        `,
    ],
})
export class RoleForm implements OnInit {
    private readonly fb = inject(FormBuilder);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly gateway = inject(RoleGateway);

    readonly state = signal<'loading' | 'ready' | 'not-found'>('ready');
    readonly submitting = signal(false);
    readonly submitError = signal('');

    mode: FormMode = 'create';

    readonly form = this.fb.nonNullable.group({
        name: ['', Validators.required],
        description: ['', Validators.maxLength(256)],
    });

    private projectId = '';
    private roleId = '';
    private original: Role | null = null;

    ngOnInit(): void {
        this.projectId = this.route.snapshot.paramMap.get('projectId') ?? '';
        this.roleId = this.route.snapshot.paramMap.get('roleId') ?? '';
        this.mode = this.roleId ? 'edit' : 'create';

        if (this.mode === 'edit') {
            this.state.set('loading');
            this.gateway.getRole(this.projectId, this.roleId).subscribe({
                next: (role) => {
                    this.original = role;
                    this.form.setValue({
                        name: role.name,
                        description: role.description,
                    });
                    this.state.set('ready');
                },
                error: (err: Error) => {
                    if (this.isNotFound(err)) {
                        this.state.set('not-found');
                    } else {
                        this.state.set('ready');
                        this.submitError.set(err.message);
                    }
                },
            });
        }
    }

    private isNotFound(err: Error): boolean {
        return err instanceof RoleError && (err.kind === 'not-found' || err.status === 404);
    }

    isInvalid(control: 'name' | 'description'): boolean {
        const c = this.form.controls[control];
        return c.invalid && (c.dirty || c.touched);
    }

    cancelLink(): string[] {
        return this.mode === 'edit' ? ['../../../roles', this.roleId] : ['../../roles'];
    }

    submit(): void {
        if (this.form.invalid || this.submitting()) {
            this.form.markAllAsTouched();
            return;
        }
        this.submitError.set('');
        this.mode === 'edit' ? this.saveUpdate() : this.saveCreate();
    }

    private saveCreate(): void {
        const value = this.form.getRawValue();
        const body: CreateRoleRequest = {
            name: value.name,
            description: value.description,
        };
        this.submitting.set(true);
        this.gateway.createRole(this.projectId, body).subscribe({
            next: (role) => {
                this.router.navigate(['../../roles', role.id], { relativeTo: this.route });
            },
            error: (err: Error) => {
                this.submitting.set(false);
                this.submitError.set(err.message);
            },
        });
    }

    private saveUpdate(): void {
        const value = this.form.getRawValue();
        const body: UpdateRoleRequest = {};
        if (value.name !== this.original?.name) {
            body.name = value.name;
        }
        if (value.description !== this.original?.description) {
            body.description = value.description;
        }

        // Nothing changed — return to detail without a needless request.
        if (Object.keys(body).length === 0) {
            this.router.navigate(['../../../roles', this.roleId], { relativeTo: this.route });
            return;
        }

        this.submitting.set(true);
        this.gateway.updateRole(this.projectId, this.roleId, body).subscribe({
            next: (role) => {
                this.router.navigate(['../../../roles', role.id], { relativeTo: this.route });
            },
            error: (err: Error) => {
                this.submitting.set(false);
                this.submitError.set(err.message);
            },
        });
    }
}
